import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Volume2, VolumeX, HelpCircle, RotateCcw, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DealtCard from '../components/blackjack/DealtCard';
import RulesCard from '../components/blackjack/RulesCard';
import './BlackjackV2.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const A = '/games/blackjack/assets';
const CHIPS = [
    { v: 5, img: `${A}/BJ-07A_Chip_5.png` },
    { v: 10, img: `${A}/BJ-07B_Chip_10.png` },
    { v: 25, img: `${A}/BJ-07C_Chip_25.png` },
    { v: 50, img: `${A}/BJ-07D_Chip_50.png` },
    { v: 100, img: `${A}/BJ-07E_Chip_100.png` },
];
const MAX_HAND = 100;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const playGameSound = (src, muted, volume = 0.5) => {
    if (muted || typeof Audio === 'undefined') return;
    const audio = new Audio(src);
    audio.volume = volume;
    audio.play().catch(() => {});
};
const playTurnChime = (muted) => {
    if (muted || typeof window === 'undefined') return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    try {
        const context = new AudioCtx();
        const start = context.currentTime;
        [[659.25, 0], [880, 0.11]].forEach(([frequency, offset]) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            oscillator.type = 'sine';
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, start + offset);
            gain.gain.exponentialRampToValueAtTime(0.11, start + offset + 0.018);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.22);
            oscillator.connect(gain).connect(context.destination);
            oscillator.start(start + offset);
            oscillator.stop(start + offset + 0.24);
        });
        setTimeout(() => context.close(), 500);
    } catch { /* Audio is optional; gameplay must continue if a browser blocks it. */ }
};

const resultAsset = (hand) => {
    if (hand?.result === 'bj') return 'BJ-14D_BLACKJACK.png';
    if (hand?.result === 'win') return 'BJ-14A_WIN.png';
    if (hand?.result === 'push') return 'BJ-14B_PUSH.png';
    if (hand?.status === 'bust') return 'BJ-14E_BUST.png';
    if (hand?.result === 'lose') return 'BJ-14C_LOSE.png';
    return null;
};

const Chip = ({ denom, selected, onClick }) => (
    <button type="button" onClick={onClick} data-testid={onClick ? `bjv2-chip-${denom.v}` : undefined}
        className={`bjv2-chip ${selected ? 'bjv2-chip--sel' : ''}`} aria-label={`$${denom.v} chip`}>
        <img src={denom.img} alt={`$${denom.v}`} draggable={false} />
    </button>
);

const ChipStack = ({ amount }) => {
    if (!amount) return null;
    const stack = [];
    let rem = amount;
    [100, 50, 25, 10, 5].forEach((d) => {
        while (rem >= d && stack.length < 4) {
            stack.push(d);
            rem = Math.round((rem - d) * 100) / 100;
        }
    });
    if (!stack.length) stack.push(5);
    return (
        <span className="bjv2-stack" aria-hidden>
            {stack.map((d, i) => (
                <span key={`${d}-${i}`} className="bjv2-stack__disc" style={{ transform: `translate(-50%, ${-i * 4}px)` }}>
                    <img src={CHIPS.find((x) => x.v === d)?.img || CHIPS[0].img} alt="" />
                </span>
            ))}
        </span>
    );
};

export default function BlackjackV2() {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const [muted, setMuted] = useState(() => localStorage.getItem('bj-muted') === '1');
    const [rulesOpen, setRulesOpen] = useState(false);
    const [chipIdx, setChipIdx] = useState(2);
    const [mainBets, setMainBets] = useState([0, 0, 0]);
    const [betoBets, setBetoBets] = useState([0, 0, 0]);
    const [placements, setPlacements] = useState([]);
    const [activeSpot, setActiveSpot] = useState(1);
    const [busy, setBusy] = useState(false);
    const [game, setGame] = useState(null);
    const [lastBets, setLastBets] = useState(null);
    const [settlementReady, setSettlementReady] = useState(false);
    const [playerRevealReady, setPlayerRevealReady] = useState(false);
    const blackjackSoundsPlayed = useRef(new Set());

    const chip = CHIPS[chipIdx].v;
    const balance = Number(user?.credits || 0) + Number(user?.bonus_credits || 0);
    const totalBet = useMemo(
        () => mainBets.reduce((s, b) => s + b, 0) + betoBets.reduce((s, b) => s + b, 0),
        [mainBets, betoBets],
    );

    const toggleMute = () => setMuted((m) => {
        localStorage.setItem('bj-muted', !m ? '1' : '0');
        return !m;
    });

    const placeMain = (hand) => {
        setActiveSpot(hand);
        if ((mainBets[hand] || 0) + chip > MAX_HAND) {
            toast.error(`Max $${MAX_HAND} per hand`);
            return;
        }
        playGameSound('/games/blackjack/audio/chip-drop.mp3', muted, 0.42);
        setMainBets((prev) => {
            const next = [...prev];
            next[hand] = Math.round((next[hand] + chip) * 100) / 100;
            return next;
        });
        setPlacements((prev) => [...prev, { hand, type: 'main', amount: chip }]);
    };

    const placeBeto = (hand) => {
        setActiveSpot(hand);
        const main = mainBets[hand] || 0;
        if (!main) {
            toast.error('Place a main bet on this hand first');
            return;
        }
        if ((betoBets[hand] || 0) + chip > main) {
            toast.error('BETO cannot exceed the main bet');
            return;
        }
        playGameSound('/games/blackjack/audio/chip-drop.mp3', muted, 0.42);
        setBetoBets((prev) => {
            const next = [...prev];
            next[hand] = Math.round((next[hand] + chip) * 100) / 100;
            return next;
        });
        setPlacements((prev) => [...prev, { hand, type: 'beto', amount: chip }]);
    };

    const undo = () => {
        const last = placements[placements.length - 1];
        if (!last) return;
        const setter = last.type === 'main' ? setMainBets : setBetoBets;
        setter((prev) => {
            const next = [...prev];
            next[last.hand] = Math.max(0, Math.round((next[last.hand] - last.amount) * 100) / 100);
            return next;
        });
        setPlacements((prev) => prev.slice(0, -1));
    };

    const clearAll = () => {
        setMainBets([0, 0, 0]);
        setBetoBets([0, 0, 0]);
        setPlacements([]);
    };

    const rebet = () => {
        try {
            const last = JSON.parse(localStorage.getItem('bjv2-last') || 'null');
            if (!last) return toast.message('No previous bet to rebet');
            setMainBets(last.mainBets);
            setBetoBets(last.betoBets);
            setPlacements([]);
        } catch {
            toast.error('Previous bet could not be restored');
        }
    };

    const deal = async (bets = mainBets, sides = betoBets) => {
        const seats = bets.map((b, i) => ({ b, i })).filter((seat) => seat.b > 0);
        if (!seats.length) return toast.error('Place a bet on at least one hand first');
        if (seats.some((seat) => seat.b < 0.5)) return toast.error('Minimum bet is $0.50');
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/blackjack/deal`, {
                bets: seats.map((seat) => seat.b),
                holics_bonus: seats.map((seat) => sides[seat.i] || 0),
                match_dealer: seats.map(() => 0),
            }, { withCredentials: true });
            setGame(data);
            setLastBets({ mainBets: bets, betoBets: sides });
            localStorage.setItem('bjv2-last', JSON.stringify({ mainBets: bets, betoBets: sides }));
            if (data.status !== 'done') refreshUser?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Deal failed');
        } finally { setBusy(false); }
    };

    const doAction = async (action, handIdx = null, body = {}) => {
        if (!game || busy) return;
        setBusy(true);
        try {
            const payload = handIdx === null ? body : { hand_idx: handIdx, ...body };
            const { data } = await axios.post(`${API}/blackjack/${game.id}/${action}`, payload, { withCredentials: true });
            setGame(data);
            if (data.status !== 'done') refreshUser?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || `${action} failed`);
        } finally { setBusy(false); }
    };

    const newBets = () => { setGame(null); clearAll(); };
    const rebetAndDeal = () => {
        if (!lastBets) return;
        setGame(null);
        setMainBets(lastBets.mainBets);
        setBetoBets(lastBets.betoBets);
        deal(lastBets.mainBets, lastBets.betoBets);
    };

    const activeIndex = game?.active_hand;
    const activeHand = activeIndex === null || activeIndex === undefined ? null : game?.hands?.[activeIndex];
    const canDouble = !!activeHand && activeHand.status === 'playing' && activeHand.cards?.length === 2;
    const canSplit = canDouble && activeHand.cards?.[0]?.[0] === activeHand.cards?.[1]?.[0]
        && (game?.hands?.length || 0) < 6 && balance >= Number(activeHand.bet || 0);
    const insurancePrompt = game?.insurance_available && game?.insurance_taken === null && game?.status === 'playing';
    const playerCardSignature = (game?.hands || []).map((hand) => hand.cards?.length || 0).join(',');

    useEffect(() => {
        if (!game) {
            setPlayerRevealReady(false);
            return undefined;
        }
        setPlayerRevealReady(false);
        const counts = (game.hands || []).map((hand) => hand.cards?.length || 0);
        const initialDeal = counts.length > 0 && counts.every((count) => count <= 2);
        const maxCards = Math.max(2, ...counts);
        const revealDelay = initialDeal ? ((counts.length * 2) + 2) * 180 + 650 : 950 + Math.max(0, maxCards - 3) * 650;
        const timer = setTimeout(() => setPlayerRevealReady(true), revealDelay);
        return () => clearTimeout(timer);
    }, [game?.id, game?.active_hand, game?.status, playerCardSignature]);

    useEffect(() => {
        if (playerRevealReady && game?.status === 'playing' && game?.active_hand !== null && game?.active_hand !== undefined) {
            playTurnChime(muted);
        }
    }, [playerRevealReady, game?.active_hand, game?.status, muted]);

    useEffect(() => {
        if (!playerRevealReady || !game?.id) return;
        (game.hands || []).forEach((hand, index) => {
            if (hand.result !== 'bj') return;
            const key = `${game.id}-${index}`;
            if (blackjackSoundsPlayed.current.has(key)) return;
            blackjackSoundsPlayed.current.add(key);
            playGameSound('/games/blackjack/audio/blackjack-win.mp3', muted, 0.24);
        });
    }, [playerRevealReady, game?.id, game?.hands, muted]);

    useEffect(() => {
        if (game?.status !== 'done') {
            setSettlementReady(false);
            return undefined;
        }
        const dealerCards = Math.max(2, game.dealer?.cards?.length || 0);
        const finishDelay = 1300 + Math.max(0, dealerCards - 2) * 800;
        const timer = setTimeout(() => {
            setSettlementReady(true);
            refreshUser?.();
        }, finishDelay);
        return () => clearTimeout(timer);
    }, [game?.id, game?.status, game?.dealer?.cards?.length]);

    const dealerCardDelay = (index) => {
        if (game?.status === 'done') return index === 0 ? 0 : 0.45 + ((index - 1) * 0.8);
        const handCount = game?.hands?.length || 1;
        if (index === 0) return handCount * 0.18;
        if (index === 1) return ((handCount * 2) + 1) * 0.18;
        return 0.45 + ((index - 1) * 0.8);
    };

    return (
        <div className="bjv2-root" data-testid="blackjack-v2-page">
            <header className="bjv2-header">
                <button onClick={() => navigate('/lobby')} className="bjv2-hbtn" data-testid="bjv2-back" aria-label="Back"><ChevronLeft size={20} /></button>
                <div className="bjv2-wallet" data-testid="bjv2-wallet"><span className="bjv2-wallet__amt">{money(balance)}</span></div>
                <div className="bjv2-logowrap"><img src={`${A}/BJ-03_Blackjack_Logo.png`} alt="Betohalics Blackjack" className="bjv2-logo" draggable={false} /></div>
                <div className="bjv2-hactions">
                    <button onClick={toggleMute} className="bjv2-hbtn" data-testid="bjv2-mute" aria-label="Sound">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
                    <button onClick={() => setRulesOpen(true)} className="bjv2-hbtn" data-testid="bjv2-rules-btn" aria-label="Rules"><HelpCircle size={18} /></button>
                </div>
            </header>

            <main className="bjv2-table" data-testid="bjv2-stage">
                <div className="bjv2-wall" style={{ backgroundImage: `url(${A}/BJ-01_Background_Table_Plate.png)` }} aria-hidden />
                <div className="bjv2-felt" style={{ backgroundImage: `url(${A}/BJ-01_Background_Table_Plate.png)` }} aria-hidden />
                <img src={`${A}/BJ-02_Glowing_Spade_Emblem.png`} alt="" className="bjv2-emblem-img" draggable={false} />
                <img src={`${A}/BJ-04_Dealer_Card_Shoe.png`} alt="" className="bjv2-shoe-img" draggable={false} />

                {!game && <>
                    <svg className="bjv2-arc" viewBox="0 0 853 220" aria-hidden="true">
                        <defs><path id="bjv2-arc-title" d="M 95 145 Q 426 15 758 145" /><path id="bjv2-arc-rule" d="M 120 160 Q 426 72 733 160" /></defs>
                        <text className="bjv2-arc-title"><textPath href="#bjv2-arc-title" startOffset="50%" textAnchor="middle">BETOHALICS BLACKJACK</textPath></text>
                        <text className="bjv2-arc-rule"><textPath href="#bjv2-arc-rule" startOffset="50%" textAnchor="middle">DEALER MUST STAND ON ALL 17s · INSURANCE PAYS 2 TO 1</textPath></text>
                    </svg>
                    <p className="bjv2-helper" data-testid="bjv2-helper">TAP A BETTING SPOT TO PLACE CHIPS</p>
                    {[{ h: 0, x: 16.5, y: 48.2 }, { h: 2, x: 83.5, y: 48.2 }, { h: 1, x: 50, y: 51.6 }].map(({ h, x, y }) => (
                        <section key={h} className={`bjv2-hand ${activeSpot === h ? 'is-active' : ''}`} style={{ left: `${x}%`, top: `${y}%` }} data-testid={`bjv2-hand-${h}`}>
                            <span className="bjv2-hand__lbl" style={{ backgroundImage: `url(${A}/${'BJ-13A_Inactive_Label_Capsule'}.png)` }}>HAND {h + 1}</span>
                            <button className="bjv2-spot bjv2-spot--main" onClick={() => placeMain(h)} data-testid={`bjv2-main-${h}`}>
                                {activeSpot === h && <img src={`${A}/BJ-15_Active_Hand_Halo.png`} alt="" className="bjv2-halo" draggable={false} />}
                                <img src={`${A}/BJ-05_Main_Bet_Spot.png`} alt="" className="bjv2-spot__art" draggable={false} />
                                <ChipStack amount={mainBets[h]} />
                                {mainBets[h] > 0 && <span className="bjv2-wager" data-testid={`bjv2-wager-${h}`}>{money(mainBets[h])}</span>}
                            </button>
                            <button className="bjv2-spot bjv2-spot--beto" onClick={() => placeBeto(h)} data-testid={`bjv2-beto-${h}`}>
                                <img src={`${A}/BJ-06_BETO_Side_Bet_Spot.png`} alt="BETO side bet" className="bjv2-spot__art" draggable={false} />
                                <ChipStack amount={betoBets[h]} />
                            </button>
                        </section>
                    ))}
                </>}

                {game && <section className={`bjv2-game ${game.hands?.length > 3 ? 'bjv2-game--compact' : ''}`} data-testid="bjv2-gameplay">
                    <div className="bjv2-dealer">
                        <span className="bjv2-table-label">DEALER</span>
                        <div className="bjv2-cardrow">{(game.dealer?.cards || []).map((card, index) => <DealtCard key={`dealer-${index}`} card={card === '??' ? null : card} faceDown={card === '??'} reveal={card !== '??'} delay={dealerCardDelay(index)} muted={muted} />)}</div>
                        {!!game.dealer?.totals?.total && (game.status !== 'done' || settlementReady) && <span className="bjv2-total-pill">{game.dealer.totals.total}{game.dealer.totals.bust ? ' · BUST' : ''}</span>}
                    </div>
                    <div className="bjv2-play-hands">{(game.hands || []).map((hand, index) => {
                        const active = playerRevealReady && game.status === 'playing' && game.active_hand === index;
                        const badge = !playerRevealReady || (game.status === 'done' && !settlementReady) ? null : resultAsset(hand);
                        const cardCount = hand.cards?.length || 0;
                        const overlap = cardCount <= 3 ? -7 : cardCount === 4 ? -17 : cardCount === 5 ? -23 : -27;
                        return <article key={`play-${index}`} className={`bjv2-play-hand ${active ? 'is-active' : ''}`}>
                            {active && <img src={`${A}/BJ-15_Active_Hand_Halo.png`} alt="" className="bjv2-play-halo" />}
                            <span className="bjv2-table-label">HAND {index + 1}</span>
                            <div className="bjv2-cardrow" style={{ '--bjv2-ml': `${overlap}px` }}>{(hand.cards || []).map((card, cardIndex) => <DealtCard key={`${index}-${cardIndex}`} card={card} delay={(cardIndex === 0 ? index * .18 : cardIndex === 1 ? ((game.hands?.length || 1) + 1 + index) * .18 : (cardIndex - 2) * .65)} muted={muted} />)}</div>
                            {!!hand.totals?.total && playerRevealReady && <span className="bjv2-total-pill">{hand.totals.total}{hand.totals.soft ? ' · SOFT' : ''}</span>}
                            {badge && <img src={`${A}/${badge}`} alt={hand.result || hand.status} className="bjv2-result-badge" />}
                        </article>;
                    })}</div>
                </section>}
            </main>

            {!game && <footer className="bjv2-hud" style={{ backgroundImage: `url(${A}/BJ-10_Betting_HUD_Frame.png)` }} data-testid="bjv2-console">
                <div className="bjv2-total"><span className="bjv2-total__lbl">TOTAL BET</span><span className="bjv2-total__dot" aria-hidden /><span className="bjv2-total__amt" data-testid="bjv2-total">{money(totalBet)}</span></div>
                <div className="bjv2-chiprow">{CHIPS.map((denom, index) => <Chip key={denom.v} denom={denom} selected={chipIdx === index} onClick={() => setChipIdx(index)} />)}</div>
                <div className="bjv2-actions">
                    <button className="bjv2-mini" onClick={undo} disabled={!placements.length} data-testid="bjv2-undo"><RotateCcw size={18} /><span>UNDO</span></button>
                    <button className="bjv2-mini" onClick={clearAll} disabled={!totalBet} data-testid="bjv2-clear"><X size={18} /><span>CLEAR</span></button>
                    <button className="bjv2-deal" style={{ backgroundImage: `url(${A}/BJ-12A_Green_Button_Plate.png)` }} onClick={() => deal()} disabled={!totalBet || busy} data-testid="bjv2-deal">DEAL</button>
                    <button className="bjv2-rebet" style={{ backgroundImage: `url(${A}/BJ-12B_Gold_Button_Plate.png)` }} onClick={rebet} data-testid="bjv2-rebet">REBET</button>
                </div>
                <button className="bjv2-ruleslink" onClick={() => setRulesOpen(true)} data-testid="bjv2-rules-console">RULES &amp; PAYOUTS</button>
            </footer>}

            {game && <footer className="bjv2-gamehud" style={{ backgroundImage: `url(${A}/BJ-11_Gameplay_HUD_Frame.png)` }}>
                {insurancePrompt && playerRevealReady ? <div className="bjv2-insurance"><span>INSURANCE?</span><button onClick={() => doAction('insurance', null, { take: true })} disabled={busy}>YES</button><button onClick={() => doAction('insurance', null, { take: false })} disabled={busy}>NO</button></div>
                    : game.status === 'playing' && !playerRevealReady ? <div className="bjv2-settlement"><strong>DEALING…</strong></div>
                    : game.status === 'done' && !settlementReady ? <div className="bjv2-settlement"><strong>DEALER PLAYING…</strong></div>
                    : game.status === 'done' ? <div className="bjv2-settlement">
                        <strong className={Number(game.net || 0) >= 0 ? 'is-win' : 'is-loss'}>{Number(game.net || 0) >= 0 ? '+' : '−'}{money(Math.abs(Number(game.net || 0)))}</strong>
                        <button className="bjv2-settle-primary" onClick={rebetAndDeal} disabled={!lastBets || busy}>REBET &amp; DEAL</button>
                        <button className="bjv2-settle-secondary" onClick={newBets}>NEW BETS</button>
                    </div> : <div className="bjv2-play-actions">
                        <button className="bjv2-action bjv2-action--hit" onClick={() => doAction('hit', activeIndex)} disabled={busy}>HIT</button>
                        <button className="bjv2-action bjv2-action--stand" onClick={() => doAction('stand', activeIndex)} disabled={busy}>STAND</button>
                        <button className="bjv2-action bjv2-action--double" onClick={() => doAction('double', activeIndex)} disabled={busy || !canDouble || balance < Number(activeHand?.bet || 0)}>DOUBLE</button>
                        <button className="bjv2-action bjv2-action--split" onClick={() => doAction('split', activeIndex)} disabled={busy || !canSplit}>SPLIT</button>
                    </div>}
            </footer>}

            {rulesOpen && <div className="bjv2-modal" onClick={() => setRulesOpen(false)} data-testid="bjv2-rules-modal">
                <div className="bjv2-modal__body" onClick={(event) => event.stopPropagation()}><button className="bjv2-modal__close" onClick={() => setRulesOpen(false)} data-testid="bjv2-rules-close">✕</button><RulesCard /></div>
            </div>}
        </div>
    );
}
